import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, CalendarDays } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { openCalendly } from "@/lib/calendly";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-gradient-to-br from-slate-50 to-purple-50/40"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-2"
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-purple-600">
            Contact
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-black mt-3 tracking-tight text-slate-900 leading-[1.05]">
            Talk to a real funding specialist.
          </h2>
          <p className="mt-5 text-slate-500 text-lg">
            Have a question? Prefer to speak with a human? We're here every
            business day.
          </p>

          <button
            data-testid="contact-book-call-btn"
            onClick={openCalendly}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-purple-600 text-white font-bold px-7 py-3.5 text-sm tracking-[0.15em] uppercase transition-colors"
          >
            <CalendarDays className="w-4 h-4" /> Book a Call
          </button>

          <div className="mt-8 space-y-4">
            <a
              href="tel:+13322301915"
              className="flex items-center gap-4 group"
              data-testid="contact-phone"
            >
              <span className="w-12 h-12 rounded-2xl bg-white border border-purple-100 grid place-items-center text-purple-600 group-hover:bg-purple-600 group-hover:text-white transition">
                <Phone className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Phone</p>
                <p className="font-display font-bold text-slate-900">(332) 230-1915</p>
              </div>
            </a>
            <a
              href="mailto:hello@rocketcapfunding.com"
              className="flex items-center gap-4 group"
              data-testid="contact-email"
            >
              <span className="w-12 h-12 rounded-2xl bg-white border border-purple-100 grid place-items-center text-purple-600 group-hover:bg-purple-600 group-hover:text-white transition">
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Email</p>
                <p className="font-display font-bold text-slate-900">hello@rocketcapfunding.com</p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-2xl bg-white border border-purple-100 grid place-items-center text-purple-600">
                <MapPin className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Serving</p>
                <p className="font-display font-bold text-slate-900">All 50 U.S. States</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-3 bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-slate-100"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <input
              data-testid="contact-name"
              required
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition"
            />
            <input
              data-testid="contact-email-input"
              required
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition"
            />
          </div>
          <input
            data-testid="contact-phone-input"
            placeholder="Phone (optional)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition"
          />
          <textarea
            data-testid="contact-message"
            required
            rows={5}
            placeholder="How can we help?"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition resize-none"
          />
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={submitting}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-bold px-7 py-3.5 text-sm uppercase tracking-widest shadow-xl hover:-translate-y-0.5 transition"
          >
            {submitting ? "Sending…" : "Send message"}
            <Send className="w-4 h-4" />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
